import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PageHeading, density, radius, typography } from "@aeriosiq/ui";
import {
  DownloadIcon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from "@aeriosiq/ui/icons";

import {
  useIiContractOptionsQuery,
  useIiContractPositionOptionsQuery,
} from "../../inspection-instructions/useInspectionInstructionFacets.js";
import { LockStateChip } from "../LockStateChip.js";
import { TimeTrackingFormDialog } from "../TimeTrackingFormDialog.js";
import { StandbyTypeChip } from "../StandbyTypeChip.js";
import {
  formatDateTime,
  formatHours,
  formatMoney,
  monthInputValue,
  monthValueToRange,
} from "../format.js";
import {
  useDeleteTimeTrackingMutation,
  useLockTimeTrackingsMutation,
  useTimeTrackingConfigQuery,
  useTimeTrackingsQuery,
} from "../hooks.js";
import { isLockedConflict } from "../timeTrackingFetch.js";
import {
  STANDBY_TYPES,
  type TimeTracking,
  type TimeTrackingListFilters,
} from "../types.js";
import { buildCsv, csvFilename, typeTotals } from "./totals.js";
import { useTimeTrackingReviewAccess } from "./useTimeTrackingReviewAccess.js";

/**
 * TT-03 — back-office review, `/projects/:projectId/time-trackings`. Mockup C.
 *
 * The business question this page answers: *"the subcontractor charged 10
 * technical standbys — do 10 trackings exist?"* Filter to his period, count,
 * compare. There is no automatic matching in the MVP; the rule "not tracked in
 * our system → we don't pay it" is enforced by a human reading this table.
 *
 * **The scope filter IS the anchor hierarchy.** Whole project → one park
 * (contract) → one turbine (contract position). Whole-project uses the BE's
 * `?projectId=` roll-up (contract TT-D6), which reaches every anchor level in
 * one query — a pilot may well have booked the same day's standby at three
 * different levels.
 *
 * The story's third level reads "one inspection instruction". TT-01 settled
 * that the MVP pilot UI NEVER produces an `inspection_instruction` anchor —
 * turbine bookings anchor to the contract position so review and invoicing
 * aggregate where invoices are raised. An II scope would therefore always
 * return zero rows, so the third level is the turbine. Same hierarchy, the
 * level the data actually lives at.
 *
 * Desktop-only by IA (`routeDevice.ts`): a nine-column comparison table with
 * multi-select and invoice text is not a phone job.
 */
export function TimeTrackingReviewPage() {
  const { projectId = "" } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    canReview,
    canLock,
    isPending: accessPending,
  } = useTimeTrackingReviewAccess();

  const filters = useMemo(
    () => reviewFiltersFromSearchParams(searchParams, projectId),
    [searchParams, projectId],
  );

  /**
   * Write one or more URL params.
   *
   * Takes a PATCH OBJECT rather than a single key, and applies it through
   * `setSearchParams`'s updater form. Two sequential single-key calls in one
   * handler both read the same captured `searchParams` and the second silently
   * discards the first — which is exactly what "pick a scope, then clear the
   * stale anchor id" does. The patch shape makes that impossible to write.
   */
  const setParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value == null || value === "") next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // The chosen SCOPE LEVEL is UI state, kept in the URL beside the filters. It
  // is deliberately NOT part of the wire filter set: a level with no id picked
  // yet must keep showing the whole project, not narrow to "every contract-
  // anchored row", which is what sending a bare entityType would do.
  const scope = searchParams.get("scope") ?? "project";
  // Turbine scope needs its park picked first — the cascade endpoint is keyed
  // by contract, and a flat turbine list across a 60-turbine project is not a
  // dropdown anyone can use.
  const parkForTurbine = searchParams.get("park") ?? "";
  const parkOptions = useIiContractOptionsQuery(
    scope === "contract" || scope === "contract_position" ? projectId : null,
  );
  const turbineOptions = useIiContractPositionOptionsQuery(
    scope === "contract_position" && parkForTurbine ? parkForTurbine : null,
  );

  const listQuery = useTimeTrackingsQuery(filters, { enabled: canReview });
  const configQuery = useTimeTrackingConfigQuery(projectId, {
    enabled: canReview,
  });
  const lockMutation = useLockTimeTrackingsMutation();
  const deleteMutation = useDeleteTimeTrackingMutation();

  // TT-04 — back office adds / changes / removes trackings from HERE, the
  // surface it actually works on. The pilot page is framed as "my" time and
  // derives its anchor from the caller's own assignments, which a back-office
  // user does not have.
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TimeTracking | null>(null);

  const items = useMemo(() => listQuery.data?.items ?? [], [listQuery.data]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // A selection belongs to the filter it was made under. Narrowing the scope,
  // the period or the pilot would otherwise leave rows checked that are no
  // longer on screen — and "Mark 4 as invoiced" would lock rows the operator
  // cannot see. Clearing on any filter change is the honest behaviour; the
  // lock payload is intersected with the visible set as well (see handleLock).
  const filterKey = searchParams.toString();
  useEffect(() => {
    setSelected(new Set());
  }, [filterKey]);

  // Only unlocked rows are selectable — locking an already-locked row is a
  // no-op server-side, and offering it would suggest the lock can be redone.
  const selectableIds = useMemo(
    () => items.filter((r) => !r.isLocked).map((r) => r.timeTrackingId),
    [items],
  );
  const selectedRows = useMemo(
    () => items.filter((r) => selected.has(r.timeTrackingId)),
    [items, selected],
  );
  // The bar prices the SELECTION when there is one, else the whole filtered
  // set — the number the back office is looking at is the one they are about
  // to put on an invoice.
  const pricedRows = selectedRows.length > 0 ? selectedRows : items;
  const totals = useMemo(
    () => typeTotals(pricedRows, configQuery.data),
    [pricedRows, configQuery.data],
  );
  const currency = configQuery.data?.currency ?? "EUR";

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === selectableIds.length ? new Set() : new Set(selectableIds),
    );
  }, [selectableIds]);

  const handleLock = useCallback(async () => {
    setConfirmOpen(false);
    setNotice(null);
    // Intersect with what is ON SCREEN. Locking is irreversible, so the payload
    // is exactly the rows the operator was looking at when they confirmed —
    // never an id that survived in state after its row left the view.
    const ids = selectedRows.map((r) => r.timeTrackingId);
    if (ids.length === 0) return;
    try {
      const res = await lockMutation.mutateAsync(ids);
      setSelected(new Set());
      setNotice(
        res.alreadyLockedCount > 0
          ? `${String(res.lockedCount)} marked as invoiced; ${String(res.alreadyLockedCount)} were already locked.`
          : `${String(res.lockedCount)} marked as invoiced.`,
      );
    } catch {
      setNotice("Could not mark the selection as invoiced.");
    }
  }, [lockMutation, selectedRows]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((row: TimeTracking) => {
    setEditing(row);
    setFormOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (row: TimeTracking) => {
      setNotice(null);
      try {
        await deleteMutation.mutateAsync(row.timeTrackingId);
      } catch (error) {
        // Locked underneath us — explain, don't shout. The refetch replaces
        // the stale row either way.
        setNotice(
          isLockedConflict(error)
            ? "That tracking has just been invoiced, so it can no longer be deleted."
            : "Could not delete the time tracking.",
        );
      }
    },
    [deleteMutation],
  );

  const handleExport = useCallback(() => {
    const csv = buildCsv(items);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = csvFilename(
      items[0]?.projectLabel ?? null,
      new Date().toISOString(),
    );
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [items]);

  if (accessPending) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress aria-label="Checking permissions" />
      </Stack>
    );
  }

  if (!canReview) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" data-testid="tt-review-forbidden">
          Time-tracking review is available to project managers, back office and
          tenant administrators.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: "100%" }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/projects" underline="hover">
          Projects
        </Link>
        <Link
          component={RouterLink}
          to={`/projects/${encodeURIComponent(projectId)}`}
          underline="hover"
        >
          {items[0]?.projectLabel ?? "Project"}
        </Link>
        <Typography color="text.primary">Time trackings</Typography>
      </Breadcrumbs>

      <PageHeading>Time trackings</PageHeading>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Verify a subcontractor&apos;s invoice line by line, then lock what you
        billed.
      </Typography>

      {notice ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => {
            setNotice(null);
          }}
          data-testid="tt-review-notice"
        >
          {notice}
        </Alert>
      ) : null}

      <ReviewFilters
        filters={filters}
        scope={scope}
        parkOptions={parkOptions}
        turbineOptions={turbineOptions}
        parkForTurbine={parkForTurbine}
        onParkForTurbineChange={(id) => {
          setParams({ park: id || null, anchorId: null });
        }}
        pilots={items}
        onChange={setParams}
        onExport={handleExport}
        onCreate={openCreate}
        exportDisabled={items.length === 0}
        canLock={canLock}
        selectedCount={selected.size}
        onLock={() => {
          setConfirmOpen(true);
        }}
      />

      {listQuery.isPending ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress aria-label="Loading time trackings" />
        </Stack>
      ) : listQuery.isError ? (
        <Alert severity="error">Could not load the time trackings.</Alert>
      ) : items.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: radius.card }}>
          <Typography variant="body1">
            No time trackings match this filter.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If a subcontractor charged standby for this period, there is no
            record of it here — which is the answer the check is looking for.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: radius.card }}
        >
          <Table
            size="small"
            aria-label="Time trackings"
            data-testid="tt-review-table"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      selectableIds.length > 0 &&
                      selected.size === selectableIds.length
                    }
                    indeterminate={
                      selected.size > 0 && selected.size < selectableIds.length
                    }
                    onChange={toggleAll}
                    disabled={!canLock || selectableIds.length === 0}
                    inputProps={{
                      "aria-label": "Select all unlocked trackings",
                    }}
                  />
                </TableCell>
                <TableCell sx={headCellSx}>Pilot</TableCell>
                <TableCell sx={headCellSx}>Type</TableCell>
                <TableCell sx={headCellSx}>Applies to</TableCell>
                <TableCell sx={headCellSx}>Start</TableCell>
                <TableCell sx={headCellSx} align="right">
                  Duration
                </TableCell>
                <TableCell sx={headCellSx}>Description (→ invoice)</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={headCellSx} align="right">
                  {/* row actions */}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow
                  key={row.timeTrackingId}
                  data-testid="tt-review-row"
                  data-locked={row.isLocked ? "true" : "false"}
                  // A muted GROUND, not a dimmed foreground: opacity on the
                  // row composites every cell's text toward the background and
                  // fails WCAG AA. The row still reads as settled; the words
                  // stay legible. The lock chip is the primary signal.
                  sx={{
                    backgroundColor: row.isLocked ? "action.hover" : undefined,
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(row.timeTrackingId)}
                      onChange={() => {
                        toggleRow(row.timeTrackingId);
                      }}
                      disabled={row.isLocked || !canLock}
                      inputProps={{
                        "aria-label": `Select tracking ${row.anchorLabel}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.userLabel ?? "—"}</TableCell>
                  <TableCell>
                    <StandbyTypeChip activityType={row.activityType} dense />
                  </TableCell>
                  <TableCell>{row.anchorLabel}</TableCell>
                  <TableCell sx={numericSx}>
                    {formatDateTime(row.startTime)}
                  </TableCell>
                  <TableCell sx={numericSx} align="right">
                    {formatHours(row.durationMinutes)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: "40ch" }}>
                    {/* The full text on hover — this IS the invoice text, so
                        it must be readable without opening a row. */}
                    <Tooltip title={row.description ?? ""} enterDelay={300}>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {row.description ?? "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <LockStateChip isLocked={row.isLocked} dense />
                  </TableCell>
                  <TableCell align="right">
                    {/* A locked row has no actions — the 409 would refuse them
                        anyway, and offering a button that always fails is
                        worse than not offering one. */}
                    {canLock && !row.isLocked ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <IconButton
                          aria-label={`Edit time tracking ${row.anchorLabel}`}
                          size="small"
                          onClick={() => {
                            openEdit(row);
                          }}
                        >
                          <PencilIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          aria-label={`Delete time tracking ${row.anchorLabel}`}
                          size="small"
                          onClick={() => {
                            void handleDelete(row);
                          }}
                        >
                          <Trash2Icon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack
        direction="row"
        spacing={3}
        flexWrap="wrap"
        sx={{ mt: 2 }}
        data-testid="tt-review-totals"
      >
        <TotalItem
          label={selectedRows.length > 0 ? "Selection" : "Filtered"}
          value={formatHours(
            pricedRows.reduce((n, r) => n + (r.durationMinutes ?? 0), 0),
          )}
        />
        {totals.map((t) => (
          <Box key={t.activityType} data-testid={`tt-total-${t.activityType}`}>
            <Typography
              component="div"
              sx={{ ...typography.label, color: "text.secondary" }}
            >
              {t.label}
            </Typography>
            <Typography
              sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
            >
              {formatHours(t.minutes)}
              {t.reason === "invoiceable" ? (
                <> × rate = {formatMoney(t.amount, currency)}</>
              ) : null}
            </Typography>
            {t.reason !== "invoiceable" ? (
              <Typography variant="caption" color="text.secondary">
                {t.reason === "not_invoiceable"
                  ? "not invoiceable"
                  : "no rate set"}
              </Typography>
            ) : null}
          </Box>
        ))}
        <Box>
          <Typography
            component="div"
            sx={{ ...typography.label, color: "text.secondary" }}
          >
            Count
          </Typography>
          <Typography
            sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
            data-testid="tt-review-count"
          >
            {String(pricedRows.length)} trackings
          </Typography>
        </Box>
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
        }}
        aria-labelledby="tt-lock-confirm-title"
      >
        <DialogTitle id="tt-lock-confirm-title">
          Mark {String(selected.size)} trackings as invoiced?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This freezes each tracking completely — nobody can change its times
            or its description afterwards, because the printed invoice and the
            stored record must never diverge.{" "}
            <strong>It cannot be undone here</strong>: unlocking is a
            platform-administrator action and is not built yet.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<LockIcon />}
            data-testid="tt-lock-confirm"
            onClick={() => {
              void handleLock();
            }}
          >
            Mark as invoiced
          </Button>
        </DialogActions>
      </Dialog>

      {/* TT-04 — the same form the pilot uses, in back-office mode: the anchor
          comes from THIS project rather than the caller's assignments, and a
          pilot picker supplies `userId`. Editing drops back to the normal path
          because the row already knows whose time it is. */}
      <TimeTrackingFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
        editing={editing}
        onBehalfOfProject={{
          projectId,
          projectLabel: items[0]?.projectLabel ?? null,
        }}
      />
    </Box>
  );
}

// ─── Filters ─────────────────────────────────────────────────────────────────

interface CascadeQuery {
  data?: { items: { id: string; label: string }[] } | undefined;
}

function ReviewFilters({
  filters,
  scope,
  parkOptions,
  turbineOptions,
  parkForTurbine,
  onParkForTurbineChange,
  pilots,
  onChange,
  onExport,
  onCreate,
  exportDisabled,
  canLock,
  selectedCount,
  onLock,
}: {
  filters: TimeTrackingListFilters;
  scope: string;
  parkOptions: CascadeQuery;
  turbineOptions: CascadeQuery;
  parkForTurbine: string;
  onParkForTurbineChange: (id: string) => void;
  pilots: readonly TimeTracking[];
  onChange: (patch: Record<string, string | null>) => void;
  onExport: () => void;
  onCreate: () => void;
  exportDisabled: boolean;
  canLock: boolean;
  selectedCount: number;
  onLock: () => void;
}) {
  // The pilot facet is derived from the rows on screen rather than fetched:
  // one project's standby set is small, and a facet endpoint for it would be a
  // second round trip for a dropdown with three entries.
  const pilotOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of pilots) seen.set(r.userId, r.userLabel ?? r.userId);
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [pilots]);

  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      alignItems="center"
      sx={{ mb: 2 }}
      data-testid="tt-review-filters"
    >
      <TextField
        select
        label="Scope"
        value={scope}
        onChange={(e) => {
          // ONE patch: changing the level also invalidates the id picked at the
          // old level. Two sequential single-key writes would race — see
          // `setParams`.
          onChange({
            scope: e.target.value === "project" ? null : e.target.value,
            anchorId: null,
            park: null,
          });
        }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { "data-testid": "tt-review-scope" },
        }}
        sx={{ minWidth: density.comfortable.controlHeight * 4 }}
      >
        <MenuItem value="project">Whole project</MenuItem>
        <MenuItem value="contract">One park</MenuItem>
        <MenuItem value="contract_position">One turbine</MenuItem>
      </TextField>
      {scope === "contract" ? (
        <TextField
          select
          label="Park"
          value={filters.entityId ?? ""}
          onChange={(e) => {
            onChange({ anchorId: e.target.value || null });
          }}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { "data-testid": "tt-review-park" },
          }}
          sx={{ minWidth: density.comfortable.controlHeight * 5 }}
        >
          <MenuItem value="">Pick a park…</MenuItem>
          {(parkOptions.data?.items ?? []).map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      {scope === "contract_position" ? (
        <>
          <TextField
            select
            label="Park"
            value={parkForTurbine}
            onChange={(e) => {
              onParkForTurbineChange(e.target.value);
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { "data-testid": "tt-review-park" },
            }}
            sx={{ minWidth: density.comfortable.controlHeight * 5 }}
          >
            <MenuItem value="">Pick a park…</MenuItem>
            {(parkOptions.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Turbine"
            value={filters.entityId ?? ""}
            disabled={!parkForTurbine}
            onChange={(e) => {
              onChange({ anchorId: e.target.value || null });
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { "data-testid": "tt-review-turbine" },
            }}
            sx={{ minWidth: density.comfortable.controlHeight * 5 }}
          >
            <MenuItem value="">Pick a turbine…</MenuItem>
            {(turbineOptions.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </>
      ) : null}
      <TextField
        select
        label="Pilot"
        value={filters.userId ?? ""}
        onChange={(e) => {
          onChange({ pilot: e.target.value || null });
        }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { "data-testid": "tt-review-pilot" },
        }}
        sx={{ minWidth: density.comfortable.controlHeight * 4 }}
      >
        <MenuItem value="">All</MenuItem>
        {pilotOptions.map(([id, label]) => (
          <MenuItem key={id} value={id}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Type"
        value={filters.activityType ?? ""}
        onChange={(e) => {
          onChange({ type: e.target.value || null });
        }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { "data-testid": "tt-review-type" },
        }}
        sx={{ minWidth: density.comfortable.controlHeight * 4 }}
      >
        <MenuItem value="">All</MenuItem>
        {STANDBY_TYPES.map((t) => (
          <MenuItem key={t} value={t}>
            {t === "standby_weather" ? "Weather standby" : "Technical standby"}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Period"
        type="month"
        value={monthInputValue(filters.from)}
        onChange={(e) => {
          onChange({ month: e.target.value || null });
        }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { "data-testid": "tt-review-month" },
        }}
        sx={{ minWidth: density.comfortable.controlHeight * 4 }}
      />
      <TextField
        select
        label="Status"
        value={
          filters.locked === undefined ? "" : filters.locked ? "locked" : "open"
        }
        onChange={(e) => {
          onChange({ status: e.target.value || null });
        }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { "data-testid": "tt-review-status" },
        }}
        sx={{ minWidth: density.comfortable.controlHeight * 4 }}
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value="open">Unlocked</MenuItem>
        <MenuItem value="locked">Invoiced</MenuItem>
      </TextField>

      <Box sx={{ flex: 1 }} />

      {canLock ? (
        <Button
          variant="outlined"
          startIcon={<PlusIcon />}
          onClick={onCreate}
          data-testid="tt-review-new"
        >
          Book standby
        </Button>
      ) : null}
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={onExport}
        disabled={exportDisabled}
        data-testid="tt-review-export"
      >
        Export CSV
      </Button>
      {canLock ? (
        <Button
          variant="contained"
          startIcon={<LockIcon />}
          disabled={selectedCount === 0}
          onClick={onLock}
          data-testid="tt-review-lock"
        >
          Mark {String(selectedCount)} as invoiced
        </Button>
      ) : null}
    </Stack>
  );
}

function TotalItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        component="div"
        sx={{ ...typography.label, color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

const headCellSx = {
  ...typography.label,
  color: "text.secondary",
  whiteSpace: "nowrap",
} as const;

const numericSx = {
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
} as const;

// ─── URL ↔ filters ───────────────────────────────────────────────────────────

/**
 * Read the review URL params into a wire filter set.
 *
 * `projectId` is ALWAYS sent — even when the scope narrows to one park — so the
 * BE's hierarchy roll-up and the narrower anchor intersect rather than the
 * narrower one silently widening across projects. Exported for the unit tier.
 */
export function reviewFiltersFromSearchParams(
  params: URLSearchParams,
  projectId: string,
): TimeTrackingListFilters {
  const filters: TimeTrackingListFilters = { projectId, pageSize: 200 };
  // Only a COMPLETE pair narrows. A level chosen without an id yet keeps the
  // whole-project roll-up on screen — sending a bare `entityType` would
  // silently narrow to "every contract-anchored row", which is a different
  // question from the one the operator asked.
  const scope = params.get("scope");
  const anchorId = params.get("anchorId");
  if ((scope === "contract" || scope === "contract_position") && anchorId) {
    filters.entityType = scope;
    filters.entityId = anchorId;
  }
  const month = params.get("month");
  if (month) {
    const range = monthValueToRange(month);
    if (range) {
      filters.from = range.from;
      filters.to = range.to;
    }
  }
  const type = params.get("type");
  if (type === "standby_weather" || type === "standby_technical") {
    filters.activityType = type;
  }
  const pilot = params.get("pilot");
  if (pilot) filters.userId = pilot;
  const status = params.get("status");
  if (status === "locked") filters.locked = true;
  else if (status === "open") filters.locked = false;
  return filters;
}
